import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { RevenueConfigApi } from 'api/revenue-entries/revenue-config/api';
import type { RevenueConfig } from 'api/revenue-entries/revenue-config/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { queryClient } from 'queries';

export const REVENUE_CONFIG_KEY = ['revenue-config'];

export const useGetRevenueConfig = (
  queryOptions?: UseQueryOptions<
    RevenueConfig,
    unknown,
    RevenueConfig,
    string[]
  >,
) => {
  return useQuery(
    REVENUE_CONFIG_KEY,
    () => RevenueConfigApi.getRevenueConfig(),
    {
      staleTime: GENERAL_API_STALE,
      ...(queryOptions ?? {}),
    },
  );
};

// Alias for consistency with expense configuration
export const useRevenueConfiguration = useGetRevenueConfig;

export const useUpdateRevenueConfig = () => {
  return useMutation({
    mutationFn: (revenueConfig: RevenueConfig) =>
      RevenueConfigApi.updateRevenueConfig(revenueConfig),
    onMutate: async (revenueConfig) => {
      await queryClient.cancelQueries({ queryKey: REVENUE_CONFIG_KEY });
      const previousRevenueConfig =
        queryClient.getQueryData<RevenueConfig>(REVENUE_CONFIG_KEY);

      queryClient.setQueryData(REVENUE_CONFIG_KEY, revenueConfig);

      return { previousRevenueConfig };
    },
    onError: (_error, _revenueConfig, context) => {
      queryClient.setQueryData(
        REVENUE_CONFIG_KEY,
        context?.previousRevenueConfig,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: REVENUE_CONFIG_KEY });
    },
  });
};
